// src/services/userSpecificInterestRateService.js
import { toast } from 'react-toastify';
import AuthService from './authService';

class UserSpecificInterestRateService {
  static get apiBase() {
    return window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
  }

  static parseError = async (response) => {
    const fallback = { message: `Request failed with status ${response.status}` };
    const contentType = response.headers.get('content-type') || '';

    if (!contentType.includes('application/json')) {
      return fallback;
    }

    try {
      return await response.json();
    } catch {
      return fallback;
    }
  };

  // Get current active rate for a user
  static getCurrentRateByUserId = async (userId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/UserSpecificInterestRate/current/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data
        };
      } else if (response.status === 404) {
        return {
          success: false,
          message: 'Active user-specific interest rate not found',
          data: null
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Interest rate could not be retrieved',
          data: null
        };
      }
    } catch (error) {
      console.error('Error fetching user-specific interest rate:', error);
      return {
        success: false,
        message: 'Server error occurred',
        data: null
      };
    }
  };

  // Get all rates for a user
  static getRatesByUserId = async (userId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/UserSpecificInterestRate/user/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data || []
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Interest rates could not be retrieved',
          data: []
        };
      }
    } catch (error) {
      console.error('Error fetching user-specific interest rates:', error);
      return {
        success: false,
        message: 'Server error occurred',
        data: []
      };
    }
  };

  // Get all user-specific rates (Admin)
  static getAllRates = async () => {
    try {
      const response = await fetch(`${this.apiBase}/api/UserSpecificInterestRate/all`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.data || []
        };
      } else {
        const error = await this.parseError(response);
        return {
          success: false,
          message: error.message || 'Interest rates could not be retrieved',
          data: []
        };
      }
    } catch (error) {
      console.error('Error fetching user-specific interest rates:', error);
      return {
        success: false,
        message: 'Server error occurred',
        data: []
      };
    }
  };

  // Create a new user-specific rate (Admin)
  static createRate = async (rateData) => {
    try {
      const requestData = {
        UserId: rateData.userId,
        Rate: rateData.rate !== undefined && rateData.rate !== null ? rateData.rate : null,
        NakdiCommissionRate: rateData.nakdiCommissionRate !== undefined && rateData.nakdiCommissionRate !== null ? rateData.nakdiCommissionRate : null,
        GayrinakdiCommissionRate: rateData.gayrinakdiCommissionRate !== undefined && rateData.gayrinakdiCommissionRate !== null ? rateData.gayrinakdiCommissionRate : null,
        EffectiveDate: rateData.effectiveDate || null,
        ExpirationDate: rateData.expirationDate || null,
        Description: rateData.description || ''
      };

      const response = await fetch(`${this.apiBase}/api/UserSpecificInterestRate/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'User-specific interest rate created');
        return {
          success: true,
          data: result.data,
          message: result.message
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'Interest rate could not be created');
        return {
          success: false,
          message: error.message || 'Interest rate could not be created'
        };
      }
    } catch (error) {
      console.error('Error creating user-specific interest rate:', error);
      toast.error('Server error occurred');
      return {
        success: false,
        message: 'Server error occurred'
      };
    }
  };

  // Activate a rate (Admin)
  static activateRate = async (rateId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/UserSpecificInterestRate/activate/${rateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Interest rate activated');
        return {
          success: true,
          message: result.message
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'Interest rate could not be activated');
        return {
          success: false,
          message: error.message || 'Interest rate could not be activated'
        };
      }
    } catch (error) {
      console.error('Error activating interest rate:', error);
      toast.error('Server error occurred');
      return {
        success: false,
        message: 'Server error occurred'
      };
    }
  };

  // Deactivate a rate (Admin)
  static deactivateRate = async (rateId) => {
    try {
      const response = await fetch(`${this.apiBase}/api/UserSpecificInterestRate/deactivate/${rateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${AuthService.getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Interest rate deactivated');
        return {
          success: true,
          message: result.message
        };
      } else {
        const error = await this.parseError(response);
        toast.error(error.message || 'Interest rate could not be deactivated');
        return {
          success: false,
          message: error.message || 'Interest rate could not be deactivated'
        };
      }
    } catch (error) {
      console.error('Error deactivating interest rate:', error);
      toast.error('Server error occurred');
      return {
        success: false,
        message: 'Server error occurred'
      };
    }
  };
}

export default UserSpecificInterestRateService;
